<script setup lang="ts">
import { computed } from 'vue';

import { usePriceCalendarStore } from '@apps/main/new-desktop/core/stores/priceCalendar/priceCalendarStore';
import { PriceRibbonInner } from '@apps/main/new-desktop/modules/priceRibbon';

import {
  AppOutInSmoothAppearTransition,
  AppCollapseTransition,
  AppSingleSelect,
} from '@libs/ui';
import type { AppSingleSelectOption } from '@libs/ui';

import { CoreEventSource } from '../models';
import { useItineraryStore } from '../stores';

const priceCalendarStore = usePriceCalendarStore();
const itineraryStore = useItineraryStore();

interface FirstDayTripPriceRibbonMobileProps {
  showMonthSelectDropdown: boolean;
  showPriceRibbon: boolean;
  source: CoreEventSource;
  shadow?: boolean;
}

const props = defineProps<FirstDayTripPriceRibbonMobileProps>();

const isProductPage = computed(
  () =>
    props.source !== CoreEventSource.TripOverview &&
    props.source !== CoreEventSource.MonthsSelector &&
    props.source !== CoreEventSource.HotelSelection
);

function selectItineraryMonthFromAppSelectOption(
  selected: AppSingleSelectOption<string>
): void {
  const date = new Date(selected.id);
  priceCalendarStore.selectItineraryMonth(date, props.source);
}
</script>

<template>
  <div class="first-day-trip-price-ribbon-mobile">
    <div class="price-ribbon-actions">
      <slot name="left-action" />
      <div v-if="props.showMonthSelectDropdown">
        <AppOutInSmoothAppearTransition>
          <AppSingleSelect
            v-if="props.showPriceRibbon"
            type="transparent"
            size="size-s"
            :options="priceCalendarStore.monthsSelectOptions"
            :selected="priceCalendarStore.selectedMonthOption"
            :use-teleport="false"
            @select="selectItineraryMonthFromAppSelectOption"
          />

          <slot v-else name="right-action" />
        </AppOutInSmoothAppearTransition>
      </div>
    </div>
    <!--TODO: Hardcoded value for mobile calendar, find a way to not hardcode this -->
    <AppCollapseTransition height="96px">
      <div v-if="props.showPriceRibbon" class="price-ribbon-container">
        <PriceRibbonInner
          :months="priceCalendarStore.priceRibbonMonths"
          :allow-hover-actions="priceCalendarStore.allowHoverActions"
          :selected-itinerary-date="itineraryStore.selectedItinerary?.date"
          :is-product-page="isProductPage"
          :is-small-device="true"
          @select-date="
            priceCalendarStore.selectItineraryDate($event, props.source)
          "
          @select-month="
            priceCalendarStore.selectItineraryMonth($event, props.source)
          "
        />
      </div>
    </AppCollapseTransition>
    <div class="bottom-row-container">
      <slot name="bottom-row" />
    </div>
  </div>
</template>

<style scoped lang="scss">
//NOTE: be careful, changes to this file may break the price ribbon animation.
.first-day-trip-price-ribbon-mobile {
  position: relative;

  .price-ribbon-actions {
    background-color: var(--color-white);
    color: var(--color-neutral-70);
    position: relative;
    z-index: 3;
    display: flex;
    justify-content: space-between;
    padding: var(--spacing-12) var(--spacing-16);
  }

  .price-ribbon-container {
    background-color: var(--color-white);
    color: var(--color-neutral-70);
    position: relative;
    z-index: 1;
    padding-bottom: 0px;
    box-shadow: var(--shadow);
  }

  .bottom-row-container {
    position: relative;
    z-index: 2;
  }
}
</style>
